import { memo } from 'react';
import { Users } from 'lucide-react';
import { SectionHeader } from '../ui/SectionHeader';
import { formatPlayers } from '../../utils/starchart';
import type { SeriesStats } from '../../utils/starchart';
import type { StarchartSummaryResponse } from '../../types/starchart';

interface PlayerSummaryCardProps {
  /** Server-computed summary, which is also the only source of percentiles. */
  summary: StarchartSummaryResponse | null;
  /** Client-side fallback when the summary endpoint failed for this server. */
  fallback: SeriesStats;
}

const Metric = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="subcard p-3">
    <div className="eyebrow">{label}</div>
    <div className="text-lg font-semibold text-white tabular-nums mt-0.5">{value}</div>
    {hint && <div className="text-xs text-glass-500 mt-0.5">{hint}</div>}
  </div>
);

/**
 * Player counts over the selected range.
 *
 * Prefers the API's own summary, which computes percentiles server-side and saves reducing tens
 * of thousands of samples in the browser. That endpoint answers HTTP 500 for an unrecognised
 * server, so the client-side statistics stand in when it fails.
 */
export const PlayerSummaryCard = memo(({ summary, fallback }: PlayerSummaryCardProps) => {
  const peak = summary?.peak ?? fallback.max;
  const average = summary?.average ?? fallback.average;
  const min = summary?.min ?? fallback.min;
  const percentiles = summary?.percentiles ?? {};

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Players in range"
        icon={Users}
        description={summary ? undefined : 'Calculated locally — the summary endpoint was unavailable.'}
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <Metric label="Peak" value={formatPlayers(peak)} />
        <Metric label="Average" value={formatPlayers(average)} />
        <Metric label="Minimum" value={formatPlayers(min)} />
        <Metric
          label="Samples"
          value={fallback.count.toLocaleString()}
          hint={
            fallback.valueCount !== fallback.count
              ? `${(fallback.count - fallback.valueCount).toLocaleString()} with no reading`
              : undefined
          }
        />
      </div>

      {Object.keys(percentiles).length > 0 && (
        <div className="grid grid-cols-3 gap-3 mt-3">
          {['p25', 'p50', 'p75'].map((key) =>
            percentiles[key] !== undefined ? (
              <Metric
                key={key}
                label={key === 'p50' ? 'Median' : key.toUpperCase()}
                value={formatPlayers(percentiles[key])}
                hint={
                  key === 'p50'
                    ? 'Half the time above this'
                    : key === 'p25'
                      ? 'Quiet quarter'
                      : 'Busy quarter'
                }
              />
            ) : null,
          )}
        </div>
      )}
    </div>
  );
});

PlayerSummaryCard.displayName = 'PlayerSummaryCard';
